import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { inventoryAPI } from '@/lib/api';
import { resolveImageUrl } from '@/lib/imageUtils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ContactPopup } from '@/components/ContactPopup';
import { SEO, buildBreadcrumbSchema, buildInventoryListSchema } from '@/components/SEO';
import { Search, SlidersHorizontal, X, Gauge, MapPin, ChevronLeft, ChevronRight, ArrowUpDown, GitCompare, Cog, Zap } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_12492ec0-1e26-4f4b-96d9-e0a655349093/artifacts/4uh3nwhf_image.png";
const formatPrice = (p) => p ? `$${p.toLocaleString()}` : 'Call for Price';
const formatKM = (km) => km ? `${km.toLocaleString()} KM` : 'N/A';

const EQUIPMENT_TYPES = [
  { value: 'truck', label: 'Trucks' },
  { value: 'trailer', label: 'Trailers' },
  { value: 'dry_van', label: 'Dry Vans' },
  { value: 'reefer', label: 'Reefers' },
  { value: 'flatbed', label: 'Flatbeds' },
  { value: 'other', label: 'Other' },
];

function FilterPanel({ facets, filters, setFilters, onApply }) {
  return (
    <div className="space-y-5" data-testid="filter-panel">
      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2 block">Type</label>
        <div className="flex flex-wrap gap-2">
          <button
            className={`px-3 py-1.5 text-xs rounded-sm border font-medium transition-colors ${
              !filters.type ? 'bg-[#E31937] text-white border-[#E31937]' : 'border-zinc-200 text-zinc-600 hover:border-zinc-400'
            }`}
            onClick={() => setFilters(f => ({ ...f, type: '' }))}
            data-testid="filter-type-All"
          >All</button>
          {EQUIPMENT_TYPES.map(t => (
            <button
              key={t.value}
              className={`px-3 py-1.5 text-xs rounded-sm border font-medium transition-colors ${
                filters.type === t.value ? 'bg-[#E31937] text-white border-[#E31937]' : 'border-zinc-200 text-zinc-600 hover:border-zinc-400'
              }`}
              onClick={() => setFilters(f => ({ ...f, type: t.value }))}
              data-testid={`filter-type-${t.value}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2 block">Make</label>
        <Select value={filters.make || 'all'} onValueChange={(v) => setFilters(f => ({ ...f, make: v === 'all' ? '' : v }))}>
          <SelectTrigger data-testid="filter-make"><SelectValue placeholder="All Makes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Makes</SelectItem>
            {facets.makes?.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2 block">Body Type</label>
        <Select value={filters.body_type || 'all'} onValueChange={(v) => setFilters(f => ({ ...f, body_type: v === 'all' ? '' : v }))}>
          <SelectTrigger data-testid="filter-body-type"><SelectValue placeholder="All Body Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Body Types</SelectItem>
            {facets.body_types?.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2 block">Year Range</label>
        <div className="flex gap-2">
          <Input type="number" placeholder="Min" value={filters.year_min || ''} onChange={(e) => setFilters(f => ({ ...f, year_min: e.target.value }))} className="h-9" data-testid="filter-year-min" />
          <Input type="number" placeholder="Max" value={filters.year_max || ''} onChange={(e) => setFilters(f => ({ ...f, year_max: e.target.value }))} className="h-9" data-testid="filter-year-max" />
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2 block">Mileage (KM)</label>
        <div className="flex gap-2">
          <Input type="number" placeholder="Min KM" value={filters.mileage_min || ''} onChange={(e) => setFilters(f => ({ ...f, mileage_min: e.target.value }))} className="h-9" data-testid="filter-mileage-min" />
          <Input type="number" placeholder="Max KM" value={filters.mileage_max || ''} onChange={(e) => setFilters(f => ({ ...f, mileage_max: e.target.value }))} className="h-9" data-testid="filter-mileage-max" />
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2 block">Price Range</label>
        <div className="flex gap-2">
          <Input type="number" placeholder="Min $" value={filters.price_min || ''} onChange={(e) => setFilters(f => ({ ...f, price_min: e.target.value }))} className="h-9" data-testid="filter-price-min" />
          <Input type="number" placeholder="Max $" value={filters.price_max || ''} onChange={(e) => setFilters(f => ({ ...f, price_max: e.target.value }))} className="h-9" data-testid="filter-price-max" />
        </div>
      </div>
      <Button className="w-full bg-[#E31937] hover:bg-[#C4122C] text-white" onClick={onApply} data-testid="apply-filters">
        Apply Filters
      </Button>
      <Button variant="ghost" className="w-full text-zinc-500" onClick={() => setFilters({})} data-testid="clear-filters">
        Clear All
      </Button>
    </div>
  );
}

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [facets, setFacets] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);
  const [compareList, setCompareList] = useState([]);
  const [mobileFilters, setMobileFilters] = useState(false);
  const [contactPopup, setContactPopup] = useState(false);
  const navigate = useNavigate();

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, search: search || undefined, sort, page, limit: 12, public: true };
      Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
      const res = await inventoryAPI.list(params);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
      setFacets(res.data.facets || {});
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filters, search, sort, page]);

  useEffect(() => { fetchInventory(); }, [fetchInventory]);

  const toggleCompare = (item) => {
    setCompareList(prev => {
      if (prev.find(c => c.id === item.id)) return prev.filter(c => c.id !== item.id);
      if (prev.length >= 4) return prev;
      return [...prev, item];
    });
  };

  const activeFilters = Object.entries(filters).filter(([k, v]) => v);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="inventory-page">
      <SEO
        title={`Used Trucks & Trailers For Sale${activeFilters.length > 0 ? ' - Filtered' : ''}`}
        description={`Browse ${total}+ premium used trucks and trailers at Canadian Truck Sales, Mississauga ON. Volvo, Freightliner, Kenworth, Peterbilt, International & more. Best prices in the GTA. Day cabs, sleepers, reefer trailers, dry vans. Financing available for all credit types.`}
        url="/inventory"
        keywords="used trucks for sale Ontario, used trailers for sale Ontario, used Volvo trucks, used Freightliner Cascadia, used Kenworth T680, used Peterbilt 579, used reefer trailers, used dry van trailers, truck dealer Mississauga, buy used truck GTA, used semi trucks Canada, used class 8 trucks Ontario, affordable used trucks Ontario, commercial trucks for sale near me"
        jsonLd={[buildBreadcrumbSchema([{ name: 'Home', url: '/' }, { name: 'Inventory', url: '/inventory' }]), buildInventoryListSchema(items)]}
      />
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold uppercase text-[#0A0A0A]">Inventory</h1>
          <p className="text-zinc-500 text-sm mt-1">{total} vehicles available</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              placeholder="Search inventory..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchInventory()}
              className="pl-9 h-9"
              data-testid="search-input"
            />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-40 h-9" data-testid="sort-select">
              <ArrowUpDown className="w-3 h-3 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest Arrivals</SelectItem>
              <SelectItem value="price_low">Price: Low to High</SelectItem>
              <SelectItem value="price_high">Price: High to Low</SelectItem>
              <SelectItem value="mileage_low">Mileage: Low to High</SelectItem>
              <SelectItem value="mileage_high">Mileage: High to Low</SelectItem>
              <SelectItem value="year_new">Year: Newest</SelectItem>
              <SelectItem value="year_old">Year: Oldest</SelectItem>
            </SelectContent>
          </Select>
          <Sheet open={mobileFilters} onOpenChange={setMobileFilters}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="outline" size="icon" className="h-9 w-9" data-testid="mobile-filter-trigger">
                <SlidersHorizontal className="w-4 h-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-[80vh] overflow-auto rounded-t-xl">
              <h3 className="text-lg font-semibold mb-4">Filters</h3>
              <FilterPanel facets={facets} filters={filters} setFilters={setFilters} onApply={() => { fetchInventory(); setMobileFilters(false); }} />
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Active filters */}
      {activeFilters.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {activeFilters.map(([k, v]) => (
            <Badge key={k} variant="secondary" className="cursor-pointer" onClick={() => setFilters(f => ({ ...f, [k]: '' }))}>
              {k}: {v} <X className="w-3 h-3 ml-1" />
            </Badge>
          ))}
        </div>
      )}

      <div className="flex gap-6">
        {/* Desktop filters */}
        <div className="hidden md:block w-60 flex-shrink-0 sticky top-20 self-start" data-testid="desktop-filters">
          <h3 className="text-sm font-semibold uppercase text-zinc-500 mb-4">Filters</h3>
          <FilterPanel facets={facets} filters={filters} setFilters={setFilters} onApply={fetchInventory} />
        </div>

        {/* Grid */}
        <div className="flex-1">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse bg-zinc-100 rounded-sm h-72" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-zinc-500 text-lg">No vehicles match your criteria</p>
              <Button variant="outline" className="mt-4" onClick={() => { setFilters({}); setSearch(''); }}>Clear Filters</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="inventory-grid">
              {items.map((item) => {
                const isCompared = compareList.some(c => c.id === item.id);
                return (
                  <Card
                    key={item.id}
                    className="group overflow-hidden rounded-sm border bg-white shadow-sm hover:shadow-lg transition-all duration-300"
                    data-testid={`inventory-card-${item.id}`}
                  >
                    <div className="relative aspect-video overflow-hidden cursor-pointer" onClick={() => navigate(`/inventory/${item.id}`)}>
                      {item.images?.length > 0 ? (
                        <img src={resolveImageUrl(item.images[0])} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full bg-zinc-200 flex flex-col items-center justify-center">
                          <img src={LOGO_URL} alt="CTS" className="w-2/5 opacity-30" />
                          <span className="mt-2 text-sm font-semibold text-zinc-400 uppercase tracking-wider">Coming Soon</span>
                        </div>
                      )}
                      {/* Logo — only on images, shifts left when ribbon present */}
                      {item.images?.length > 0 && (
                        <div className={`absolute top-2 ${item.status && item.status !== 'Available' ? 'left-2' : 'right-2'} z-[5] pointer-events-none`}>
                          <div className="bg-white/95 backdrop-blur-sm rounded px-2 py-1 shadow">
                            <img src={LOGO_URL} alt="CTS" className="h-7 w-auto" />
                          </div>
                        </div>
                      )}
                      {/* Center watermark */}
                      {item.images?.length > 0 && <img src={LOGO_URL} alt="" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2/3 opacity-[0.18] pointer-events-none z-[1] drop-shadow-sm" />}
                      <div className="absolute bottom-2 left-2 flex gap-1.5 z-[3]">
                        {item.featured && <span className="bg-[#E31937] text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm">Featured</span>}
                        <span className="bg-[#0A0A0A]/70 text-white text-[10px] px-2 py-0.5 rounded-sm capitalize">{item.type === 'dry_van' ? 'Dry Van' : item.type}</span>
                      </div>
                      {item.status && item.status !== 'Available' && (
                        <div className="absolute top-0 right-0 overflow-hidden w-32 h-32 pointer-events-none z-[4]" data-testid={`ribbon-${item.id}`}>
                          <div className="absolute top-[14px] right-[-34px] w-[160px] text-center text-xs font-extrabold uppercase tracking-widest text-white py-2 rotate-45 shadow-lg bg-[#E31937]" style={{textShadow: '0 1px 3px rgba(0,0,0,0.4)', backgroundImage: 'linear-gradient(to right, #c0102e, #E31937, #ff2d4f, #E31937, #c0102e)'}}>
                            {item.status}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <p className="text-xs text-zinc-400 uppercase tracking-wider">{item.stock_number}</p>
                      <h3 className="font-semibold text-base text-[#0A0A0A] mt-0.5 cursor-pointer hover:text-[#E31937] transition-colors truncate" onClick={() => navigate(`/inventory/${item.id}`)}>
                        {item.title}
                      </h3>
                      {/* Type-specific specs */}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-zinc-500">
                        {item.type === 'truck' && (
                          <>
                            {item.mileage_km && <span className="flex items-center gap-1"><Gauge className="w-3.5 h-3.5" />{formatKM(item.mileage_km)}</span>}
                            {item.engine && <span className="flex items-center gap-1"><Cog className="w-3.5 h-3.5" />{item.engine}</span>}
                            {item.horsepower && <span className="flex items-center gap-1"><Zap className="w-3.5 h-3.5" />{item.horsepower} HP</span>}
                          </>
                        )}
                        {item.type === 'dry_van' && (
                          <>
                            {item.floor_type && <span><b className="text-zinc-600">Floor:</b> {item.floor_type}</span>}
                            {(item.length || item.trailer_length) && <span><b className="text-zinc-600">Length:</b> {item.length || item.trailer_length}</span>}
                          </>
                        )}
                        {item.type === 'reefer' && (
                          <>
                            {item.reefer_unit && <span><b className="text-zinc-600">Unit:</b> {item.reefer_unit}</span>}
                            {item.reefer_hours && <span><b className="text-zinc-600">Hours:</b> {Number(item.reefer_hours).toLocaleString()}</span>}
                          </>
                        )}
                        {item.type === 'flatbed' && (
                          <>
                            {item.axles && <span><b className="text-zinc-600">Axle:</b> {item.axles}</span>}
                            {item.floor_type && <span><b className="text-zinc-600">Floor:</b> {item.floor_type}</span>}
                            {(item.length || item.trailer_length) && <span><b className="text-zinc-600">Length:</b> {item.length || item.trailer_length}</span>}
                          </>
                        )}
                        {item.type === 'trailer' && (
                          <>
                            {item.trailer_type && <span><b className="text-zinc-600">Type:</b> {item.trailer_type}</span>}
                            {(item.length || item.trailer_length) && <span><b className="text-zinc-600">Length:</b> {item.length || item.trailer_length}</span>}
                          </>
                        )}
                        {item.type === 'other' && (
                          <>
                            {item.condition && <span className="capitalize">{item.condition}</span>}
                          </>
                        )}
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-2.5 border-t">
                        {item.status === 'Sold' ? (
                          <p className="text-lg font-bold text-zinc-500 uppercase">Sold</p>
                        ) : (item.price && item.price_public !== false) ? (
                          <p className="text-lg font-bold text-[#E31937]">{formatPrice(item.price)}</p>
                        ) : (
                          <button className="text-lg font-bold text-[#E31937] hover:underline" onClick={(e) => { e.stopPropagation(); setContactPopup(true); }} data-testid={`call-price-${item.id}`}>
                            Call for Price
                          </button>
                        )}
                        <button
                          className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-sm transition-colors ${
                            isCompared ? 'bg-[#E31937] text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                          }`}
                          onClick={() => toggleCompare(item)}
                          data-testid={`compare-toggle-${item.id}`}
                        >
                          <GitCompare className="w-3.5 h-3.5" />
                          {isCompared ? 'Added' : 'Compare'}
                        </button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8" data-testid="pagination">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-zinc-500">Page {page} of {pages}</span>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Compare bar */}
      {compareList.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-[#0A0A0A] text-white py-3 px-4 z-50 shadow-2xl" data-testid="compare-bar">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium">Compare ({compareList.length}/4):</span>
              <div className="flex gap-2">
                {compareList.map(c => (
                  <Badge key={c.id} variant="secondary" className="bg-zinc-800 text-white cursor-pointer" onClick={() => toggleCompare(c)}>
                    {c.title?.substring(0, 20)} <X className="w-3 h-3 ml-1" />
                  </Badge>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" className="text-zinc-400 text-xs" onClick={() => setCompareList([])}>Clear</Button>
              <Button
                className="bg-[#E31937] hover:bg-[#C4122C] text-white text-xs"
                disabled={compareList.length < 2}
                onClick={() => navigate(`/compare?ids=${compareList.map(c => c.id).join(',')}`)}
                data-testid="compare-button"
              >
                Compare Now
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Popup */}
      <ContactPopup open={contactPopup} onOpenChange={setContactPopup} />
    </div>
  );
}
